import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import "@/App.css";

// ---------------------------------------------------------------------------
// Lazy-loaded pages
// ---------------------------------------------------------------------------
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const SimulatorPage = lazy(() => import("./pages/SimulatorPage"));
const EvaluationPage = lazy(() => import("./pages/EvaluationPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));

const AdminPage = lazy(() => import("./pages/AdminPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

const CompanyPersonaSettingsPage = lazy(() =>
  import("./pages/CompanyPersonaSettingsPage")
);
const CompanyAgentsPage = lazy(() => import("./pages/CompanyAgentsPage"));
const CompanyTeamsPage = lazy(() => import("./pages/CompanyTeamsPage"));
const CompanyOwnerSessionsPage = lazy(() =>
  import("./pages/CompanyOwnerSessionsPage")
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Role Definitions & Hierarchy Mapping
// ---------------------------------------------------------------------------

export const ROLES = {
  // Base Level
  LEARNER: "learner",
  AGENT: "agent",
  ADVISOR: "advisor",
  MANAGER: "manager",

  // Mid Level: Company Executive / Owner
  OWNER: "owner",
  COMPANY_OWNER: "company_owner",

  // Top Level: Super Admin
  ADMIN: "admin",
};

// Defined at module scope for stable reference comparisons in route guards
const AGENT_LEVEL_ROLES = [
  ROLES.LEARNER,
  ROLES.AGENT,
  ROLES.ADVISOR,
  ROLES.MANAGER,
  ROLES.OWNER,
  ROLES.COMPANY_OWNER,
  ROLES.ADMIN,
];

const ANALYTICS_ROLES = [
  ROLES.MANAGER,
  ROLES.OWNER,
  ROLES.COMPANY_OWNER,
  ROLES.ADMIN,
];

const COMPANY_OWNER_ROLES = [
  ROLES.OWNER,
  ROLES.COMPANY_OWNER,
  ROLES.ADMIN,
];

const SUPER_ADMIN_ROLES = [
  ROLES.ADMIN,
];

// ---------------------------------------------------------------------------
// Axios setup
// ---------------------------------------------------------------------------

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (process.env.NODE_ENV !== "production") {
      console.error(
        "[Axios Error]",
        error.config?.method?.toUpperCase(),
        error.config?.url,
        "status:",
        error.response?.status,
        "data:",
        error.response?.data,
        "message:",
        error.message
      );
    }
    // Auto clear expired session on 401
    if (error.response?.status === 401) {
      localStorage.removeItem("access_token");
      delete axios.defaults.headers.common["Authorization"];
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Auth Context
// ---------------------------------------------------------------------------

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function isSSORedirect() {
  return window.location.search.includes("token=");
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(
    async (overrideToken = null, preloadedUser = null) => {
      if (preloadedUser) {
        setUser(preloadedUser);
        setLoading(false);
        return preloadedUser;
      }

      const token = overrideToken || localStorage.getItem("access_token");

      if (!token) {
        setUser(false);
        setLoading(false);
        return false;
      }

      try {
        const { data } = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(data);
        return data;
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.error(
            "[Auth] checkAuth failed:",
            err?.response?.status || err.message
          );
        }
        localStorage.removeItem("access_token");
        delete axios.defaults.headers.common["Authorization"];
        setUser(false);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (isSSORedirect()) {
      if (process.env.NODE_ENV !== "production") {
        console.log(
          "[Auth] SSO redirect detected — deferring initial checkAuth to LandingPage."
        );
      }
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });
      const accessToken = data.access_token || data.token;
      if (accessToken) {
        localStorage.setItem("access_token", accessToken);
        axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      }
      setUser(data);
      return { success: true };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  }, []);

  const register = useCallback(
    async (email, password, name, role = ROLES.AGENT) => {
      try {
        const { data } = await axios.post(`${API}/auth/register`, {
          email,
          password,
          name,
          role,
        });
        const accessToken = data.access_token || data.token;
        if (accessToken) {
          localStorage.setItem("access_token", accessToken);
          axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
        }
        setUser(data);
        return { success: true };
      } catch (e) {
        return {
          success: false,
          error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
        };
      }
    },
    []
  );

  const loginWithSSOToken = useCallback(
    async (accessToken) => {
      localStorage.setItem("access_token", accessToken);
      axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
      const result = await checkAuth(accessToken);
      return !!result;
    },
    [checkAuth]
  );

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("[Auth] Logout request failed:", e.message);
      }
    }
    localStorage.removeItem("access_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(false);
  }, []);

  const userRole = user?.role?.toLowerCase();
  const isAgentOrManager = useMemo(
    () => [ROLES.LEARNER, ROLES.AGENT, ROLES.ADVISOR, ROLES.MANAGER].includes(userRole),
    [userRole]
  );
  const isCompanyOwner = useMemo(
    () => [ROLES.OWNER, ROLES.COMPANY_OWNER].includes(userRole),
    [userRole]
  );
  const isSuperAdmin = userRole === ROLES.ADMIN;

  const authValue = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      logout,
      checkAuth,
      loginWithSSOToken,
      isAgentOrManager,
      isCompanyOwner,
      isSuperAdmin,
    }),
    [
      user,
      loading,
      login,
      register,
      logout,
      checkAuth,
      loginWithSSOToken,
      isAgentOrManager,
      isCompanyOwner,
      isSuperAdmin,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function FullScreenLoader({ label = "Loading..." }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 rounded-full border-slate-200 border-t-[#0B1F3B] animate-spin"></div>
        <p className="text-xs font-semibold text-slate-600">{label}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route guards
// ---------------------------------------------------------------------------

function ProtectedRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullScreenLoader label="Loading session..." />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permission Check (Case-insensitive comparison)
  const currentRole = user.role?.toLowerCase();
  if (allowedRoles && !allowedRoles.some((r) => r.toLowerCase() === currentRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const hasToken = params.has("token");

  if (hasToken && location.pathname !== "/") {
    return <Navigate to={`/${location.search}`} replace />;
  }

  return (
    <Suspense fallback={<FullScreenLoader label="Loading page..." />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        {/* <Route path="/register" element={<RegisterPage />} /> */}

        {/* Base Operational Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={AGENT_LEVEL_ROLES}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulator"
          element={
            <ProtectedRoute allowedRoles={AGENT_LEVEL_ROLES}>
              <SimulatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/evaluation/:sessionId"
          element={
            <ProtectedRoute allowedRoles={AGENT_LEVEL_ROLES}>
              <EvaluationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/history"
          element={
            <ProtectedRoute allowedRoles={AGENT_LEVEL_ROLES}>
              <HistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/:userId"
          element={
            <ProtectedRoute allowedRoles={AGENT_LEVEL_ROLES}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute allowedRoles={AGENT_LEVEL_ROLES}>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        {/* Mid-Tier Analytics (Managers, Owners, Admins) */}
        <Route
          path="/analytics"
          element={
            <ProtectedRoute allowedRoles={ANALYTICS_ROLES}>
              <AnalyticsPage />
            </ProtectedRoute>
          }
        />

        {/* Company Administration (Company Owners & Super Admins) */}
        <Route
          path="/company/agents"
          element={
            <ProtectedRoute allowedRoles={COMPANY_OWNER_ROLES}>
              <CompanyAgentsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/teams"
          element={
            <ProtectedRoute allowedRoles={COMPANY_OWNER_ROLES}>
              <CompanyTeamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/sessions"
          element={
            <ProtectedRoute allowedRoles={COMPANY_OWNER_ROLES}>
              <CompanyOwnerSessionsPage />
            </ProtectedRoute>
          }
        />

        {/* Platform Level Administration (Super Admins Only) */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={SUPER_ADMIN_ROLES}>
              <AdminPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/company-personas"
          element={
            <ProtectedRoute allowedRoles={SUPER_ADMIN_ROLES}>
              <CompanyPersonaSettingsPage />
            </ProtectedRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    const hideBadge = () => {
      const badge = document.getElementById("emergent-badge");
      if (badge) badge.remove();
    };
    hideBadge();
    const observer = new MutationObserver(hideBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL };